import React, { useState } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import ChartsPanel from '../components/ChartsPanel';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Thermometer, Droplets, Layers } from 'lucide-react';

const OceanLayersPage = () => {
  const [layers, setLayers] = useState({
    depth: true,
    temperature: true,
    salinity: false,
  });

  const [vesselData] = useState({
    lat: -23.745,
    lon: -134.120,
    speed: 9.8,
    heading: 212,
  });

  const toggleLayer = (layer) => {
    setLayers(prev => ({
      ...prev,
      [layer]: !prev[layer],
    }));
  };

  return (
    <div 
      className="grid grid-cols-1 lg:grid-cols-[auto_1fr] lg:grid-rows-[56px_1fr] min-h-screen bg-background text-foreground"
      data-testid="ocean-layers-page-container"
    >
      <Sidebar layers={layers} toggleLayer={toggleLayer} currentRoute="/layers" />
      <Topbar vesselData={vesselData} />
      <main className="pt-14 lg:pt-0 lg:col-start-2 lg:row-start-2 p-4 md:p-6 relative z-0">
        <div className="mb-6" data-testid="page-header">
          <div className="flex items-center gap-3 mb-2">
            <Layers className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold">Ocean Layers</h1>
          </div>
          <p className="text-muted-foreground">Oceanographic data profiles including temperature, salinity, and depth measurements</p>
        </div>

        <div className="grid grid-cols-12 gap-4 md:gap-6" data-testid="ocean-layers-page-grid">
          <ChartsPanel />

          <Card className="col-span-12 lg:col-span-6" data-testid="temperature-info-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Thermometer className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium tracking-wide text-muted-foreground uppercase">Temperature Data</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="text-sm font-medium mb-3">Temperature Profile</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between py-2 border-b border-border/30">
                    <span className="text-muted-foreground">Surface Temp</span>
                    <span className="font-mono">23.4 °C</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-border/30">
                    <span className="text-muted-foreground">Thermocline Depth</span>
                    <span className="font-mono">80 m</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-border/30">
                    <span className="text-muted-foreground">Deep Water Temp</span>
                    <span className="font-mono">12.8 °C</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-muted-foreground">Temperature Range</span>
                    <span className="font-mono">10.6 °C</span>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-border/40">
                <h4 className="text-sm font-medium mb-2">Measurement Details</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Last Updated</span>
                    <span className="font-mono">2 min ago</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Sensor Type</span>
                    <span className="font-mono">CTD</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Accuracy</span>
                    <span className="font-mono">±0.01 °C</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="col-span-12 lg:col-span-6" data-testid="salinity-info-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Droplets className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium tracking-wide text-muted-foreground uppercase">Salinity Data</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="text-sm font-medium mb-3">Salinity Profile</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between py-2 border-b border-border/30">
                    <span className="text-muted-foreground">Surface Salinity</span>
                    <span className="font-mono">35.1 PSU</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-border/30">
                    <span className="text-muted-foreground">Halocline Depth</span>
                    <span className="font-mono">120 m</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-border/30">
                    <span className="text-muted-foreground">Deep Water Salinity</span>
                    <span className="font-mono">34.3 PSU</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-muted-foreground">Salinity Range</span>
                    <span className="font-mono">0.8 PSU</span>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-border/40">
                <h4 className="text-sm font-medium mb-2">Measurement Details</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Last Updated</span>
                    <span className="font-mono">2 min ago</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Sensor Type</span>
                    <span className="font-mono">CTD</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Accuracy</span>
                    <span className="font-mono">±0.01 PSU</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default OceanLayersPage;
